/**
 * Plausible Analytics for the Booking Widget
 * 
 * Privacy-friendly analytics that works when embedded on hotel websites.
 * Tracks widget loads, booking flow events, and conversions.
 * 
 * SETUP:
 * - Set VITE_PLAUSIBLE_DOMAIN in environment
 * - Optionally set VITE_PLAUSIBLE_HOST for self-hosted instances
 * - Call initPlausible() in main.tsx after Sentry init
 */

// Environment variables
const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
const PLAUSIBLE_HOST = import.meta.env.VITE_PLAUSIBLE_HOST || 'https://plausible.io';

// Track initialization state
let isInitialized = false;
let initPromise: Promise<void> | null = null;

// Widget-specific event names
export const widgetEvents = {
  widgetLoad: 'Widget Load',
  widgetError: 'Widget Error',
  widgetLoadFailed: 'Widget Load Failed',
  bookingStarted: 'Booking Started',
  bookingCompleted: 'Booking Completed',
  bookingAbandoned: 'Booking Abandoned',
  experienceViewed: 'Experience Viewed',
  experienceSearch: 'Experience Search',
  experienceListViewed: 'Experience List Viewed',
  dateSelected: 'Date Selected',
  checkoutStarted: 'Checkout Started',
  paymentStarted: 'Payment Started',
  paymentFailed: 'Payment Failed',
  promoCodeEntered: 'Promo Code Entered',
  priceDisplayed: 'Price Displayed',
  templateRender: 'Template Render',
  templateInteraction: 'Template Interaction',
  instanceLoad: 'Instance Load',
} as const;

export type WidgetEvent = typeof widgetEvents[keyof typeof widgetEvents];

// Event properties interfaces
export interface WidgetEventProps {
  [widgetEvents.widgetLoad]: {
    propertyId: string;
    propertyName?: string;
    referrer?: string;
  };
  [widgetEvents.widgetError]: {
    propertyId: string;
    error: string;
    component?: string;
    errorType?: string;
  };
  [widgetEvents.widgetLoadFailed]: {
    propertyId: string;
    error: string;
    stage?: string;
  };
  [widgetEvents.experienceViewed]: {
    propertyId: string;
    experienceId: string;
    experienceName?: string;
  };
  [widgetEvents.experienceSearch]: {
    propertyId: string;
    query: string;
    resultCount: number;
  };
  [widgetEvents.experienceListViewed]: {
    propertyId: string;
    category?: string;
    filter?: string;
    experienceCount: number;
  };
  [widgetEvents.dateSelected]: {
    propertyId: string;
    experienceId: string;
    date?: string;
  };
  [widgetEvents.bookingStarted]: {
    propertyId: string;
    experienceId: string;
    experienceName?: string;
  };
  [widgetEvents.checkoutStarted]: {
    propertyId: string;
    experienceId: string;
    guestCount?: number;
  };
  [widgetEvents.paymentStarted]: {
    propertyId: string;
    experienceId: string;
    value?: number;
    currency?: string;
  };
  [widgetEvents.bookingCompleted]: {
    propertyId: string;
    experienceId: string;
    experienceName?: string;
    value?: number;
    currency?: string;
    bookingNumber?: string;
  };
  [widgetEvents.paymentFailed]: {
    propertyId: string;
    experienceId: string;
    error?: string;
  };
  [widgetEvents.bookingAbandoned]: {
    propertyId: string;
    experienceId?: string;
    step?: 'experience' | 'date' | 'checkout' | 'payment';
  };
  [widgetEvents.promoCodeEntered]: {
    propertyId: string;
    experienceId: string;
    code: string;
    valid: boolean;
    discountAmount?: number;
  };
  [widgetEvents.priceDisplayed]: {
    propertyId: string;
    experienceId: string;
    basePrice: number;
    finalPrice: number;
    currency: string;
    hasDiscount: boolean;
  };
}

/**
 * Initialize Plausible analytics by injecting the script tag
 * Safe to call multiple times - will only initialize once
 */
export function initPlausible(): Promise<void> {
  // Return existing promise if already initializing
  if (initPromise) {
    return initPromise;
  }

  // Skip if already initialized
  if (isInitialized) {
    return Promise.resolve();
  }

  // Skip if no domain configured
  if (!PLAUSIBLE_DOMAIN) {
    if (import.meta.env.DEV) {
      console.warn('[Plausible] No domain configured, skipping initialization');
    }
    return Promise.resolve();
  }

  initPromise = new Promise<void>((resolve) => {
    // Check if script already exists (might be on host page)
    const existingScript = document.querySelector('script[data-domain="' + PLAUSIBLE_DOMAIN + '"]');
    if (existingScript) {
      isInitialized = true;
      if (import.meta.env.DEV) {
        console.warn('[Plausible] Script already present on page');
      }
      resolve();
      return;
    }

    // Create and inject script
    const script = document.createElement('script');
    script.defer = true;
    script.dataset.domain = PLAUSIBLE_DOMAIN;
    script.src = `${PLAUSIBLE_HOST}/js/script.js`;

    // Handle load success
    script.onload = () => {
      isInitialized = true;
      if (import.meta.env.DEV) {
        console.warn('[Plausible] Initialized for domain:', PLAUSIBLE_DOMAIN);
      }
      processQueue();
      resolve();
    };

    // Handle load failure gracefully
    script.onerror = () => {
      console.warn('[Plausible] Failed to load analytics script');
      // Still resolve - we don't want to block the app
      resolve();
    };

    // Inject into document
    document.head.appendChild(script);
  });

  return initPromise;
}

// Event queue for events fired before Plausible loads
const eventQueue: Array<{ event: string; props: Record<string, unknown> }> = [];

/**
 * Process queued events after Plausible loads
 */
function processQueue(): void {
  const plausible = getPlausibleFunction();
  if (!plausible) return;

  while (eventQueue.length > 0) {
    const item = eventQueue.shift();
    if (item) {
      plausible(item.event, { props: item.props });
    }
  }
}

/**
 * Get the Plausible tracking function
 */
function getPlausibleFunction(): ((event: string, options?: { props: Record<string, unknown> }) => void) | null {
  return (window as unknown as { plausible?: (event: string, options?: { props: Record<string, unknown> }) => void }).plausible ?? null;
}

/**
 * Track a custom event with Plausible
 * 
 * @param eventName - The event name to track
 * @param props - Optional properties to attach to the event
 */
export function trackWidgetEvent<T extends WidgetEvent>(
  eventName: T,
  props?: T extends keyof WidgetEventProps ? WidgetEventProps[T] : Record<string, unknown>
): void {
  // Don't track in development unless explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_PLAUSIBLE_DEV_TRACKING) {
    console.warn('[Plausible] Event:', eventName, props);
    return;
  }

  const plausible = getPlausibleFunction();
  const eventProps = props ?? {};

  if (plausible) {
    plausible(eventName, { props: eventProps });
  } else if (PLAUSIBLE_DOMAIN) {
    // Queue event if Plausible isn't loaded yet
    eventQueue.push({ event: eventName, props: eventProps });
  }
}

/**
 * Check if Plausible is initialized
 */
export function isPlausibleReady(): boolean {
  return isInitialized && getPlausibleFunction() !== null;
}
